import { getRouteSequencePrimaryAddress, getRouteSequencePrimaryTitle, resolveDisplayedOperationalSequenceLabel, resolvePmLoadingOrderCount } from "./fleetTimelinePresentation.js";
import { isFrozenDispatcherStatusCode } from "./dispatcherFreezeStatus.js";

function resolveSegmentProductLabel(segment) {
  if (!["loading", "delivery"].includes(segment?.stopType)) return "";
  return normalizeSegmentMetaValue(segment.productLabel)
    || (segment.assignmentIsManualMix ? normalizeSegmentMetaValue(segment.manualMixCurrentProductLabel) : "");
}

export function resolveGroupedSegmentProductLabels(segments = []) {
  const labels = [];
  const seen = new Set();
  for (const segment of segments || []) {
    const label = resolveSegmentProductLabel(segment);
    const key = label.toLocaleLowerCase("fr-CA");
    if (!label || seen.has(key)) continue;
    seen.add(key);
    labels.push(label);
  }
  return labels;
}

// Éléments du Gantt qui ouvrent une suite d'étapes du même produit : ils portent l'onglet produit.
export function resolveProductRunStartLabels(items = []) {
  const labelByItem = new Map();
  let previousKey = "";
  for (const item of items || []) {
    const segments = item?.kind === "group" ? item.memberSegments || [] : item?.kind === "segment" ? [item.segment] : [];
    for (const segment of segments) {
      const label = resolveSegmentProductLabel(segment);
      const key = label.toLocaleLowerCase("fr-CA");
      if (!label || key === previousKey) continue;
      previousKey = key;
      if (!labelByItem.has(item)) labelByItem.set(item, label);
    }
  }
  return labelByItem;
}

// Place de chaque onglet produit, en % de la piste, jusqu'à l'onglet produit suivant qui le recouvrirait.
export function resolveProductTabRoomPercents(items = [], labelOf = () => "", leftPercentOf = () => 0) {
  const tabItems = (items || []).filter((item) => labelOf(item));
  return new Map(tabItems.map((item, index) => [item, (index + 1 < tabItems.length ? leftPercentOf(tabItems[index + 1]) : 100) - leftPercentOf(item)]));
}

// Produits de la légende, du plus répandu (nombre de camions) au moins répandu.
export function resolveTimelineLegendProductLabels(rows = []) {
  const entries = new Map();
  for (const row of rows || []) {
    for (const label of resolveGroupedSegmentProductLabels(row?.segments)) {
      const key = label.toLocaleLowerCase("fr-CA");
      const entry = entries.get(key) || { label, count: 0 };
      entry.count += 1;
      entries.set(key, entry);
    }
  }
  return [...entries.values()].sort((left, right) => right.count - left.count).map((entry) => entry.label);
}

export function countVisibleProductBadges(widths, availableWidth, overflowWidth, gap = 3) {
  const totalWidth = widths.reduce((sum, width) => sum + width, 0) + Math.max(0, widths.length - 1) * gap;
  if (totalWidth <= availableWidth) return widths.length;
  let usedWidth = 0;
  let count = 0;
  for (const width of widths) {
    if (usedWidth + width + gap + overflowWidth > availableWidth) break;
    usedWidth += width + gap;
    count++;
  }
  return count;
}

const SEQUENCE_TABLE_MARKER_STYLE_BY_STOP_TYPE = {
  delivery: {
    backgroundColor: "var(--dispatch-sequence-delivery-marker-fill, rgb(150 138 62))",
    borderRadius: "4px",
    color: "var(--dispatch-segment-delivery-ink, var(--route-delivery-strong))",
    boxShadow: "var(--dispatch-sequence-delivery-shadow, 0 0 0 1px rgba(112,103,46,0.34), 0 0 0 4px rgba(150,138,62,0.18), 0 3px 8px rgba(15,23,42,0.12))",
  },
  loading: {
    backgroundColor: "var(--dispatch-sequence-loading-marker-fill, rgb(90 121 190))",
    borderRadius: "4px",
    color: "var(--dispatch-segment-loading-ink, var(--route-loading-ink))",
    boxShadow: "var(--dispatch-sequence-loading-shadow, 0 0 0 1px rgba(63,85,133,0.34), 0 0 0 4px rgba(90,121,190,0.18), 0 3px 8px rgba(15,23,42,0.12))",
  },
};

// Context cards use the existing PM actions; the timeline keeps its worked duration.
export function resolvePmSegmentContext(segment) {
  if (!segment?.isPmOperationalLoading) return segment;
  return {
    ...segment,
    isEndOfDayLoad: true,
    typeLabel: "Chargé en PM",
    businessStep: null,
    reassignmentAvailability: null,
    isManualBufferEditable: false,
    manualBufferStepKey: null,
  };
}

function normalizeEndOfDayLoadVisualMode(value = "") {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  return normalizedValue === "evening" || normalizedValue === "soir" ? "evening" : "next_day";
}

function resolveExplicitEndOfDayLoadVisualMode(segment = null) {
  const normalizedValue = typeof (
    segment?.endOfDayLoadTargetPeriod
    || segment?.end_of_day_load_target_period
    || segment?.target_period
    || segment?.endOfDayLoadDeliveryMode
    || segment?.end_of_day_load_delivery_mode
    || segment?.delivery_mode
  ) === "string"
    ? (
      segment?.endOfDayLoadTargetPeriod
      || segment?.end_of_day_load_target_period
      || segment?.target_period
      || segment?.endOfDayLoadDeliveryMode
      || segment?.end_of_day_load_delivery_mode
      || segment?.delivery_mode
    ).trim().toLowerCase()
    : "";

  if (normalizedValue === "evening" || normalizedValue === "soir") {
    return "evening";
  }
  if (normalizedValue === "next_day" || normalizedValue === "tomorrow" || normalizedValue === "demain" || normalizedValue === "day" || normalizedValue === "jour") {
    return "next_day";
  }

  return "";
}

function normalizeExplicitEndOfDayLoadDisplayMode(value = "") {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (normalizedValue === "evening" || normalizedValue === "soir") {
    return "evening";
  }
  if (
    normalizedValue === "next_day"
    || normalizedValue === "tomorrow"
    || normalizedValue === "demain"
    || normalizedValue === "day"
    || normalizedValue === "jour"
  ) {
    return "next_day";
  }
  return "";
}

export function resolveEndOfDayLoadVisualMode(segment = null) {
  const directMode = resolveExplicitEndOfDayLoadVisualMode(segment);
  if (directMode) {
    return directMode;
  }

  const memberSegments = Array.isArray(segment?.memberSegments) ? segment.memberSegments.filter(Boolean) : [];
  const memberMode = memberSegments
    .map((memberSegment) => resolveExplicitEndOfDayLoadVisualMode(memberSegment))
    .find(Boolean);

  return memberMode || normalizeEndOfDayLoadVisualMode("");
}

export function resolveEndOfDayLoadDisplayMode(segment = null) {
  const explicitDisplayMode = normalizeExplicitEndOfDayLoadDisplayMode(
    segment?.endOfDayLoadDisplayMode
    || segment?.end_of_day_load_display_mode,
  );
  if (explicitDisplayMode) {
    return explicitDisplayMode;
  }

  return resolveEndOfDayLoadVisualMode(segment);
}

const END_OF_DAY_LOAD_VISUAL_STYLES = {
  next_day: {
    accentColor: "rgba(184,132,32,0.52)",
    className: "border-[rgba(202,138,4,0.8)] bg-[rgba(250,204,21,0.65)] text-[rgb(113,63,18)]",
    markerStyle: {
      backgroundColor: "rgb(250 207 35)",
      boxShadow: "0 0 0 1px rgba(202,138,4,0.38), 0 0 0 4px rgba(250,204,21,0.24), 0 3px 8px rgba(15,23,42,0.12)",
    },
    surfaceClassName: "border-[rgba(184,132,32,0.42)] bg-[rgb(244,232,198)] shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]",
  },
  evening: {
    accentColor: "rgba(17,24,39,0.41)",
    className: "border-[rgba(15,23,42,0.67)] bg-[rgba(15,23,42,0.83)] text-white",
    markerStyle: {
      backgroundColor: "rgb(31 38 52)",
      boxShadow: "0 0 0 1px rgba(15,23,42,0.48), 0 0 0 4px rgba(15,23,42,0.18), 0 3px 8px rgba(15,23,42,0.16)",
    },
    surfaceClassName: "border-[rgba(15,23,42,0.24)] bg-[rgb(248,250,252)] shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]",
  },
};

function getEndOfDayLoadVisualStyle(visualMode = "next_day") {
  return END_OF_DAY_LOAD_VISUAL_STYLES[normalizeEndOfDayLoadVisualMode(visualMode)]
    || END_OF_DAY_LOAD_VISUAL_STYLES.next_day;
}

function shouldHideTripIdentifier(value) {
  return /^TRK-[A-Z0-9-]+/i.test(normalizeSegmentMetaValue(value));
}

export function getSegmentHoverCardAccentColor(stopType) {
  const isInitialPreload = arguments[1] === true;
  const isEndOfDayLoad = arguments[2] === true;
  const endOfDayLoadVisualMode = arguments[3];

  if (isEndOfDayLoad) {
    return getEndOfDayLoadVisualStyle(endOfDayLoadVisualMode).accentColor;
  }

  if (isInitialPreload) {
    return "var(--dispatch-segment-preload-accent, rgba(122,143,184,0.38))";
  }

  if (stopType === "loading") {
    return "var(--dispatch-segment-loading-accent, rgba(var(--route-loading-rgb),0.36))";
  }

  if (stopType === "delivery") {
    return "var(--dispatch-segment-delivery-accent, rgba(var(--route-delivery-rgb),0.38))";
  }

  if (stopType === "depot_start") {
    return "var(--dispatch-segment-inspection-border, var(--dispatch-segment-return-accent))";
  }

  return "var(--dispatch-segment-return-accent, rgba(15,92,87,0.34))";
}

export function getSegmentClassName(stopType, isSelected) {
  const isInitialPreload = arguments[2] === true;
  const isEndOfDayLoad = arguments[3] === true;
  const endOfDayLoadVisualMode = arguments[4];
  const baseClassName = isEndOfDayLoad
    ? getEndOfDayLoadVisualStyle(endOfDayLoadVisualMode).className
    : isInitialPreload
    ? "border-[var(--dispatch-segment-preload-border,rgba(122,143,184,0.48))] bg-[var(--dispatch-segment-preload-fill,rgba(122,143,184,0.26))] text-[var(--dispatch-segment-preload-ink,#3f5278)]"
    : stopType === "depot_start"
    ? "border-[var(--dispatch-segment-inspection-border,var(--dispatch-segment-return-border))] bg-[var(--dispatch-segment-inspection-fill,var(--dispatch-segment-return-fill))] text-[var(--dispatch-segment-return-ink,var(--color-accent-strong))]"
    : stopType === "loading"
    ? "border-[var(--dispatch-segment-loading-border,rgba(var(--route-loading-rgb),0.5))] bg-[var(--dispatch-segment-loading-fill,rgba(var(--route-loading-rgb),0.28))] text-[var(--dispatch-segment-loading-ink,var(--route-loading-ink))]"
    : stopType === "delivery"
      ? "border-[var(--dispatch-segment-delivery-border,rgba(var(--route-delivery-rgb),0.5))] bg-[var(--dispatch-segment-delivery-fill,rgba(var(--route-delivery-rgb),0.26))] text-[var(--dispatch-segment-delivery-ink,var(--route-delivery-strong))]"
      : "border-[var(--dispatch-segment-return-border,rgba(15,92,87,0.34))] bg-[var(--dispatch-segment-return-fill,rgba(15,92,87,0.18))] text-[var(--dispatch-segment-return-ink,var(--color-accent-strong))]";

  return [
    "border shadow-none",
    baseClassName,
    isSelected
      ? "ring-2 ring-[rgba(15,92,87,0.26)]"
      : "hover:border-[rgba(15,92,87,0.4)]",
  ].join(" ");
}

export function getSequenceTableMarkerStyle(stopType, fallbackColor = "") {
  const isInitialPreload = arguments[2] === true;
  const isEndOfDayLoad = arguments[3] === true;
  const endOfDayLoadVisualMode = arguments[4];

  if (isEndOfDayLoad) {
    return getEndOfDayLoadVisualStyle(endOfDayLoadVisualMode).markerStyle;
  }

  if (isInitialPreload) {
    return {
      backgroundColor: "var(--dispatch-sequence-preload-marker-fill, rgb(122 143 184))",
      boxShadow: "var(--dispatch-sequence-preload-shadow, 0 0 0 1px rgba(88,106,145,0.34), 0 0 0 4px rgba(122,143,184,0.18), 0 3px 8px rgba(15,23,42,0.12))",
    };
  }

  if (SEQUENCE_TABLE_MARKER_STYLE_BY_STOP_TYPE[stopType]) {
    return SEQUENCE_TABLE_MARKER_STYLE_BY_STOP_TYPE[stopType];
  }

  return {
    backgroundColor: fallbackColor || "var(--dispatch-sequence-return-marker-fill, var(--color-legacy-teal))",
    boxShadow: "var(--dispatch-sequence-return-shadow, 0 0 0 1px rgba(15,92,87,0.26), 0 0 0 4px rgba(15,92,87,0.14), 0 3px 8px rgba(15,23,42,0.12))",
  };
}

export function normalizeSegmentMetaValue(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function resolveSegmentTripMetaLabel(segment) {
  const tripLabel = normalizeSegmentMetaValue(segment?.tripLabel);
  return shouldHideTripIdentifier(tripLabel) ? "" : tripLabel;
}

export function resolveSegmentHoverCardSequenceLabel(segment) {
  return resolveDisplayedOperationalSequenceLabel(segment);
}

export function buildSegmentHoverCardTitle(segment) {
  const sequenceLabel = resolveSegmentHoverCardSequenceLabel(segment);

  if (segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload || segment?.isPmOperationalLoading) {
    return sequenceLabel ? `Chargé en PM · ${sequenceLabel}` : "Chargé en PM";
  }

  if (sequenceLabel) {
    return `Étape ${sequenceLabel}`;
  }

  return "Étape";
}

export function resolveSegmentPrimaryTitle(segment) {
  const routeTitle = normalizeSegmentMetaValue(getRouteSequencePrimaryTitle(segment));
  return normalizeSegmentMetaValue(segment?.businessStep?.title)
    || (routeTitle && routeTitle !== "Arrêt" ? routeTitle : "")
    || normalizeSegmentMetaValue(segment?.fromLabel)
    || routeTitle
    || "Étape";
}

export function resolveSegmentPrimaryAddress(segment) {
  return normalizeSegmentMetaValue(getRouteSequencePrimaryAddress(segment))
    || normalizeSegmentMetaValue(segment?.fromAddress)
    || "";
}

const QUEBEC_ADMINISTRATIVE_REGIONS = new Set([
  "abitibi-temiscamingue",
  "bas-saint-laurent",
  "capitale-nationale",
  "centre-du-quebec",
  "chaudiere-appalaches",
  "cote-nord",
  "estrie",
  "gaspesie-iles-de-la-madeleine",
  "lanaudiere",
  "laurentides",
  "laval",
  "mauricie",
  "monteregie",
  "montreal",
  "nord-du-quebec",
  "outaouais",
  "saguenay-lac-saint-jean",
]);

function normalizeAddressComparisonPart(value = "") {
  return typeof value === "string"
    ? value.trim().toLowerCase().normalize("NFD").replace(/\p{M}/gu, "")
    : "";
}

function isQuebecProvinceAddressPart(part = "") {
  const normalizedPart = normalizeAddressComparisonPart(part);
  return normalizedPart === "quebec" || normalizedPart === "qc" || normalizedPart === "que";
}

function isCanadianPostalCodeAddressPart(part = "") {
  return /^[a-z]\d[a-z]\s*\d[a-z]\d$/i.test(String(part || "").trim());
}

function isQuebecAdministrativeRegionAddressPart(part = "") {
  const normalizedPart = normalizeAddressComparisonPart(part).replace(/\s+/g, "-");
  return QUEBEC_ADMINISTRATIVE_REGIONS.has(normalizedPart);
}

function isTrailingAdministrativeAddressPart(part = "") {
  return isQuebecProvinceAddressPart(part)
    || isCanadianPostalCodeAddressPart(part)
    || isQuebecAdministrativeRegionAddressPart(part);
}

function joinAddressPartsThroughCity(parts = []) {
  const resolvedParts = Array.isArray(parts) ? parts.filter(Boolean) : [];

  if (!resolvedParts.length) {
    return "";
  }

  if (resolvedParts.length <= 2) {
    return resolvedParts.join(", ");
  }

  if (resolvedParts.length === 3) {
    const firstPart = resolvedParts[0] || "";
    const secondPart = resolvedParts[1] || "";

    if (/^\d+$/.test(firstPart) || /^(rue|rang|av\.|avenue|bd|boulevard|ch\.|chemin)/i.test(secondPart)) {
      return resolvedParts.join(", ");
    }

    return `${resolvedParts[0]}, ${resolvedParts[1]}`;
  }

  return resolvedParts.join(", ");
}

export function formatSegmentHoverCardAddress(address = "") {
  const trimmedAddress = typeof address === "string" ? address.trim() : "";
  if (!trimmedAddress) {
    return "";
  }

  if (trimmedAddress.includes(";")) {
    return formatSegmentHoverCardAddress(trimmedAddress.split(";")[0] || "");
  }

  let parts = trimmedAddress
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  while (parts.length > 1 && isTrailingAdministrativeAddressPart(parts[parts.length - 1])) {
    parts = parts.slice(0, -1);
  }

  while (parts.length > 3) {
    parts = parts.slice(0, -1);
  }

  return joinAddressPartsThroughCity(parts);
}

// Display only: keep the original address for routing and persistence.
export function formatStreetCityAddress(address = "") {
  const parts = typeof address === "string"
    ? address.split(";")[0].split(",").map((part) => part.trim()).filter(Boolean)
    : [];
  if (parts.length < 2) return parts.join("");

  const separateNumber = /^\d+[a-z]?(?:[-–]\d+[a-z]?)?$/i.test(parts[0]);
  const street = separateNumber ? `${parts[0]} ${parts[1]}` : parts[0];
  let localities = parts.slice(separateNumber ? 2 : 1);
  let hasRegionalHierarchy = false;
  let cityRegion = "";
  while (localities.length > 1) {
    const last = localities.at(-1);
    const normalized = normalizeAddressComparisonPart(last);
    if (/^(canada|can|ca|united states(?: of america)?|usa|us|etats-unis)$/.test(normalized)
      || /^(ontario|on|alberta|ab|british columbia|colombie-britannique|bc|manitoba|mb|saskatchewan|sk|new brunswick|nouveau-brunswick|nb|nova scotia|nouvelle-ecosse|ns|prince edward island|ile-du-prince-edouard|pe|newfoundland and labrador|terre-neuve-et-labrador|nl|yukon|yt|northwest territories|territoires du nord-ouest|nt|nunavut|nu)$/.test(normalized)
      || isQuebecProvinceAddressPart(last) || isCanadianPostalCodeAddressPart(last)) {
      localities = localities.slice(0, -1);
      continue;
    }
    if (isQuebecAdministrativeRegionAddressPart(last)) {
      // Montréal and Laval can be the municipality itself, not an extra region.
      if (["montreal", "laval"].includes(normalized)
        && !localities.slice(0, -1).some((part) => normalizeAddressComparisonPart(part) === normalized)) {
        cityRegion = last;
        break;
      }
      hasRegionalHierarchy = true;
      localities = localities.slice(0, -1);
      continue;
    }
    break;
  }
  // Expanded Québec geocoder labels end in municipality, MRC, region.
  // Simple street/locality addresses retain their first locality.
  const city = cityRegion || (hasRegionalHierarchy && localities.length >= 3
    ? localities.at(-2) : localities[0]);
  return [street, city].filter(Boolean).join(", ");
}

function labelsReferToSamePlace(left = "", right = "") {
  const normalizedLeft = normalizeAddressComparisonPart(left);
  const normalizedRight = normalizeAddressComparisonPart(right);
  return Boolean(normalizedLeft) && normalizedLeft === normalizedRight;
}

export function resolveSegmentRouteSummary(segment) {
  const fromLabel = normalizeSegmentMetaValue(segment?.routeFromLabel) || normalizeSegmentMetaValue(segment?.fromLabel);
  const toLabel = normalizeSegmentMetaValue(segment?.routeToLabel) || normalizeSegmentMetaValue(segment?.toLabel);

  if (fromLabel && toLabel) {
    return labelsReferToSamePlace(fromLabel, toLabel) ? "" : `${fromLabel} → ${toLabel}`;
  }

  return toLabel || fromLabel || "";
}

export function getSegmentHoverSurfaceClassName(stopType = "") {
  const isInitialPreload = arguments[1] === true;
  const isEndOfDayLoad = arguments[2] === true;
  const endOfDayLoadVisualMode = arguments[3];

  if (isEndOfDayLoad) {
    return getEndOfDayLoadVisualStyle(endOfDayLoadVisualMode).surfaceClassName;
  }

  if (isInitialPreload) {
    return "border-[var(--dispatch-segment-preload-hover-border,rgba(122,143,184,0.28))] bg-[image:var(--dispatch-segment-preload-hover-surface,linear-gradient(180deg,rgba(246,248,252,0.998),rgba(234,239,248,0.972)))] shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]";
  }

  if (stopType === "loading") {
    return "border-[var(--dispatch-segment-loading-hover-border,rgba(var(--route-loading-rgb),0.28))] bg-[image:var(--dispatch-segment-loading-hover-surface,linear-gradient(180deg,rgba(243,247,255,0.995),rgba(225,235,252,0.97)))] shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]";
  }

  if (stopType === "delivery") {
    return "border-[var(--dispatch-segment-delivery-hover-border,rgba(var(--route-delivery-rgb),0.3))] bg-[image:var(--dispatch-segment-delivery-hover-surface,linear-gradient(180deg,rgba(253,251,241,0.998),rgba(247,241,214,0.975)))] shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]";
  }

  return "border-[var(--dispatch-segment-return-hover-border,rgba(15,92,87,0.18))] bg-[image:var(--dispatch-segment-return-hover-surface,linear-gradient(180deg,rgba(246,250,249,0.99),rgba(235,245,243,0.96)))] shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]";
}

export function getSegmentActionSurfaceClassName(stopType = "") {
  if (stopType === "loading") {
    return "border-[var(--dispatch-segment-loading-hover-border,rgba(var(--route-loading-rgb),0.28))] bg-[image:var(--dispatch-segment-loading-action-surface,linear-gradient(180deg,rgba(245,248,255,0.998),rgba(230,238,252,0.982)))]";
  }

  if (stopType === "delivery") {
    return "border-[var(--dispatch-segment-delivery-hover-border,rgba(var(--route-delivery-rgb),0.3))] bg-[image:var(--dispatch-segment-delivery-action-surface,linear-gradient(180deg,rgba(253,251,243,0.998),rgba(247,242,217,0.982)))]";
  }

  return "border-[var(--dispatch-segment-return-hover-border,rgba(15,92,87,0.18))] bg-[image:var(--dispatch-segment-return-action-surface,linear-gradient(180deg,rgba(247,251,250,0.995),rgba(237,246,244,0.98)))]";
}

export function resolveSegmentActionMetaLabel(segment) {
  const metaLabel = normalizeSegmentMetaValue(segment?.businessStep?.metaLabel);
  if (!metaLabel) {
    return "";
  }

  const orderLabel = normalizeSegmentMetaValue(segment?.orderLabel);
  if (!orderLabel) {
    return metaLabel;
  }

  const metaParts = metaLabel
    .split(" - ")
    .map((part) => part.trim())
    .filter(Boolean);

  if (!metaParts.length) {
    return "";
  }

  if (metaParts[0]?.toLowerCase() === orderLabel.toLowerCase()) {
    metaParts.shift();
  }

  return metaParts.join(" - ");
}

export function isLinkedCommandCompanionSegment(segment, selectedSegment) {
  if (!segment || !selectedSegment) {
    return false;
  }

  const segmentKey = `${segment.assignmentId || ""}::${segment.tripId || ""}::${segment.globalSequence || ""}`;
  const selectedSegmentKey = `${selectedSegment.assignmentId || ""}::${selectedSegment.tripId || ""}::${selectedSegment.globalSequence || ""}`;
  if (!segmentKey || segmentKey === selectedSegmentKey) {
    return false;
  }

  const segmentOrderKey = normalizeSegmentMetaValue(segment?.orderLabel).toLowerCase();
  const selectedOrderKey = normalizeSegmentMetaValue(selectedSegment?.orderLabel).toLowerCase();
  if (!segmentOrderKey || segmentOrderKey !== selectedOrderKey) {
    return false;
  }

  const segmentStopType = normalizeSegmentMetaValue(segment?.stopType).toLowerCase();
  const selectedStopType = normalizeSegmentMetaValue(selectedSegment?.stopType).toLowerCase();
  if (!segmentStopType || !selectedStopType || segmentStopType === selectedStopType) {
    return false;
  }

  return ["loading", "delivery"].includes(segmentStopType) && ["loading", "delivery"].includes(selectedStopType);
}

export function resolveSegmentWorkOrderLabel({ businessStep = null, segment = null } = {}) {
  const workOrderLabel = segment?.workOrderLabel
    || businessStep?.workOrderNumber
    || businessStep?.work_order_number
    || "";
  return normalizeSegmentMetaValue(workOrderLabel);
}

export function buildSegmentInfoItems({ businessStep = null, row = null, segment = null }) {
  const seenValues = new Set();
  const segmentStopType = normalizeSegmentMetaValue(segment?.stopType).toLowerCase();
  const operationOwnerLabel = normalizeSegmentMetaValue(
    businessStep?.orderDetails?.deliveryClientName
    || segment?.operationOwnerLabel
    || "",
  );
  const loadingClientLabel = normalizeSegmentMetaValue(
    businessStep?.orderDetails?.loadingClientName
    || "",
  );
  const primaryTitle = normalizeSegmentMetaValue(resolveSegmentPrimaryTitle(segment));
  const contextualPartyValue = segmentStopType === "delivery"
    ? loadingClientLabel
    : operationOwnerLabel;
  const contextualPartyItem = segmentStopType === "delivery"
    ? {
        key: "supplier",
        label: "Fournisseur",
         value: contextualPartyValue && contextualPartyValue.toLowerCase() !== primaryTitle.toLowerCase()
           ? contextualPartyValue
           : "",
      }
    : {
        key: "client",
        label: "Client",
         value: contextualPartyValue && contextualPartyValue.toLowerCase() !== primaryTitle.toLowerCase()
           ? contextualPartyValue
           : "",
      };

  const pmOrderCount = resolvePmLoadingOrderCount(segment);
  return [
    pmOrderCount > 1
      // Chargé PM de plusieurs commandes : le nombre, la liste est sous le résumé de la bulle.
      ? { key: "order", label: "", title: segment.endOfDayLoadOrderIds.join(", "), value: `${pmOrderCount} CMD` }
      : { key: "order", label: "Cmd", value: businessStep?.orderId || segment?.orderLabel || "" },
    contextualPartyItem,
  ].filter(Boolean).reduce((items, item) => {
    const normalizedValue = normalizeSegmentMetaValue(item.value);
    if (!normalizedValue || (item.key === "order" && segment?.isGroupActionTarget === true)) {
      return items;
    }

    const dedupeKey = normalizedValue.toLowerCase();
    if (seenValues.has(dedupeKey)) {
      return items;
    }

    seenValues.add(dedupeKey);
    items.push({
      ...item,
      value: normalizedValue,
    });
    return items;
  }, []);
}

const DELIVERY_ORDER_LOCATION_INFO_DEFINITIONS = [
  { fieldName: "loading_silo", key: "silo", label: "Silo", titleLabel: "Silo" },
  { fieldName: "building", key: "building", label: "Bât.", titleLabel: "Bâtiment" },
];

export function buildDeliveryOrderLocationInfoItems(segments = []) {
  const deliverySegments = (Array.isArray(segments) ? segments : [])
    .filter((segment) => normalizeSegmentMetaValue(segment?.stopType).toLowerCase() === "delivery");

  return DELIVERY_ORDER_LOCATION_INFO_DEFINITIONS.reduce((items, definition) => {
    const valuesByKey = new Map();

    deliverySegments.forEach((segment) => {
      const editableOrder = segment?.businessStep?.orderDetails?.editableOrder;
      const value = normalizeSegmentMetaValue(editableOrder?.[definition.fieldName]);
      const valueKey = value.toLowerCase();

      if (valueKey && !valuesByKey.has(valueKey)) {
        valuesByKey.set(valueKey, value);
      }
    });

    const values = Array.from(valuesByKey.values());
    if (!values.length) {
      return items;
    }

    const value = values.join(" · ");
    items.push({
      key: definition.key,
      label: definition.label,
      title: `${definition.titleLabel} : ${value}`,
      value,
    });
    return items;
  }, []);
}

export function getSegmentApprovalBlockingReason(segment) {
  const confirmButton = segment?.businessStep?.confirmButton || null;
  const explicitTitle = typeof confirmButton?.title === "string" ? confirmButton.title.trim() : "";

  if (!confirmButton?.disabled) {
    return "";
  }

  if (confirmButton?.isBlocked && explicitTitle) {
    return explicitTitle;
  }

  return "Action temporairement indisponible pendant la mise à jour.";
}

export function formatAssignableTargetCount(count) {
  if (!Number.isFinite(Number(count)) || Number(count) <= 0) {
    return "";
  }

  const safeCount = Number(count);
  return `${safeCount} ${safeCount > 1 ? "cibles" : "cible"}`;
}

export function buildSegmentActionFeedbackLines(segment) {
  const businessStep = segment?.businessStep || null;
  const approvalBlockingReason = getSegmentApprovalBlockingReason(segment);
  const dispatcherStatusCode = typeof businessStep?.dispatcherStatus?.code === "string"
    ? businessStep.dispatcherStatus.code
    : "";
  const shouldHideDispatcherActions = isFrozenDispatcherStatusCode(dispatcherStatusCode)
    || dispatcherStatusCode === "delivered";
  const lines = [];

  if (businessStep?.summaryTitle && businessStep.summaryTitle !== approvalBlockingReason) {
    lines.push(businessStep.summaryTitle);
  }

  if (businessStep?.dispatcherStatus?.description) {
    lines.push(businessStep.dispatcherStatus.description);
  }

  if (
    businessStep?.manualStatus?.description
    && !businessStep?.releaseButton
    && businessStep?.manualStatus?.code !== "protected"
  ) {
    lines.push(businessStep.manualStatus.description);
  }

  if (businessStep?.dispatcherActionButton?.disabled && businessStep?.dispatcherActionButton?.title) {
    lines.push(`Action ${businessStep.dispatcherActionButton.title.toLowerCase()} temporairement indisponible pendant une mise à jour en cours.`);
  }

  if (!shouldHideDispatcherActions && businessStep?.reassignButton?.disabled && businessStep?.reassignButton?.title) {
    const isProtectedReassignBlock = businessStep?.manualStatus?.code === "protected";
    if (!isProtectedReassignBlock) {
      lines.push(businessStep.reassignButton.title);
    }
  } else if (!shouldHideDispatcherActions && !businessStep?.reassignButton && segment?.reassignmentAvailability && !segment.reassignmentAvailability.hasAssignableTarget) {
    lines.push(segment.reassignmentAvailability.unavailableReason);
  }

  if (
    !shouldHideDispatcherActions
    &&
    businessStep?.confirmButton?.isBlocked
    && businessStep?.confirmButton?.title
    && businessStep.confirmButton.title !== approvalBlockingReason
  ) {
    lines.push(businessStep.confirmButton.title);
  }

  if (!shouldHideDispatcherActions && businessStep?.suspendButton?.disabled && businessStep?.suspendButton?.title) {
    const isProtectedSuspendBlock = businessStep?.manualStatus?.code === "protected";
    if (!isProtectedSuspendBlock) {
      lines.push(businessStep.suspendButton.title);
    }
  }

  return Array.from(new Set(lines.filter((line) => typeof line === "string" && line.trim())));
}

function segmentHasEndOfDayLoadEligibleDeliveries(segment) {
  const businessStep = segment?.businessStep || null;
  const deliveryIds = [];
  const appendDeliveryId = (value) => {
    const normalizedValue = normalizeSegmentMetaValue(value);
    if (normalizedValue && !deliveryIds.includes(normalizedValue)) {
      deliveryIds.push(normalizedValue);
    }
  };

  appendDeliveryId(businessStep?.deliveryId);
  appendDeliveryId(segment?.segmentReorder?.deliveryId);

  const editableOrder = businessStep?.orderDetails?.editableOrder;
  if (editableOrder && typeof editableOrder === "object") {
    if (Array.isArray(editableOrder.deliveries)) {
      editableOrder.deliveries.forEach((delivery) => {
        appendDeliveryId(delivery?.delivery_id || delivery?.deliveryId || delivery?.id);
      });
    }
    appendDeliveryId(editableOrder?.delivery_id || editableOrder?.deliveryId);
  }

  return deliveryIds.length > 0;
}

export function shouldHideSupersededSegmentConfirmButton(businessStep = null) {
  const confirmButton = businessStep?.confirmButton;
  if (!confirmButton || !businessStep?.releaseButton) {
    return false;
  }

  return confirmButton.disabled === true && confirmButton.isActive !== true;
}

export function getSegmentActionPopoverWidthClass(segment) {
  const businessStep = segment?.businessStep || null;
  if (segment?.isGroupActionTarget === true) {
    return "w-fit min-w-[min(24rem,calc(100vw-1.5rem))] max-w-[min(32rem,calc(100vw-1.5rem))]";
  }

  if (segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload || segment?.isPmOperationalLoading) {
    return "w-[min(26rem,calc(100vw-1.5rem))] max-w-[calc(100vw-1.5rem)]";
  }

  const hideSupersededConfirmButton = shouldHideSupersededSegmentConfirmButton(businessStep);
  const dispatcherStatusCode = typeof businessStep?.dispatcherStatus?.code === "string"
    ? businessStep.dispatcherStatus.code
    : "";
  const shouldHideDispatcherActions = isFrozenDispatcherStatusCode(dispatcherStatusCode)
    || dispatcherStatusCode === "delivered";
  const hasEndOfDayLoadAction = !shouldHideDispatcherActions
    && businessStep?.stepType === "loading"
    && segmentHasEndOfDayLoadEligibleDeliveries(segment);
  const actionCount = [
    { button: businessStep?.dispatcherActionButton, isDispatcherStatusAction: true },
    { button: hideSupersededConfirmButton ? null : businessStep?.confirmButton },
    { button: businessStep?.reassignButton },
    { button: hasEndOfDayLoadAction },
    { button: businessStep?.releaseButton },
  ].filter((action) => {
    if (!action?.button) {
      return false;
    }

    if (action.isDispatcherStatusAction) {
      return true;
    }

    return !shouldHideDispatcherActions;
  }).length;

  if (actionCount >= 5) {
    return "w-fit min-w-[min(29rem,calc(100vw-1.5rem))] max-w-[min(32rem,calc(100vw-1.5rem))]";
  }

  if (businessStep?.releaseButton && !shouldHideDispatcherActions) {
    return "w-fit min-w-[min(24rem,calc(100vw-1.5rem))] max-w-[min(29rem,calc(100vw-1.5rem))]";
  }

  if (actionCount >= 4) {
    return "w-fit min-w-[min(22rem,calc(100vw-1.5rem))] max-w-[min(27rem,calc(100vw-1.5rem))]";
  }

  if (actionCount === 3) {
    return "w-fit min-w-[min(19.5rem,calc(100vw-1.5rem))] max-w-[min(24rem,calc(100vw-1.5rem))]";
  }

  return "w-fit min-w-[min(17.5rem,calc(100vw-1.5rem))] max-w-[min(21.5rem,calc(100vw-1.5rem))]";
}
